import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import type { LinkKind, Point, SwmmLink, SwmmProject } from "../model/inp/types.ts";
import type { VertexTarget } from "../model/inp/editNetwork.ts";
import { polygonCentroid } from "../model/inp/bounds.ts";
import { kindLabelKey } from "../i18n/fieldLabels.ts";
import type { Translate } from "../i18n/hooks.ts";

/** A map element's own accessible name, its kind included — `"J1, button"` alone gives a screen reader no context on what kind of element it even is. Shared by NetworkMap.tsx's own SVG shapes and GeoreferencedMap.tsx's Leaflet ones. Falls back to the bare name for an unrecognized kind (shouldn't arise for a real element, but never worth crashing over). */
export function elementAriaLabel(t: Translate, kind: string, name: string): string {
  const kindKey = kindLabelKey(kind);
  return kindKey ? t("networkMap.elementAriaLabel", { kind: t(kindKey), name }) : name;
}

/** Node/link subtypes in display order, shared with App.tsx's per-kind visibility map. Subcatchments/raingages have no subtype of their own, so ALL_KINDS folds them in as 1-element categories to keep that map uniform across all 4 element categories. */
export const NODE_KIND_ORDER = ["junction", "outfall", "storage", "divider"] as const;
export const LINK_KIND_ORDER: LinkKind[] = ["conduit", "pump", "orifice", "weir", "outlet"];
export const ALL_KINDS: string[] = [
  ...NODE_KIND_ORDER,
  ...LINK_KIND_ORDER,
  "subcatchment",
  "raingage",
];

// NODE_COLOR/LINK_COLOR/LINK_DASH used to live here — moved to
// ./mapTheme.ts (UI redesign phase 2), which is now the single source of
// truth for every map/legend color, not just these three. Import from
// there instead.

export function isSelected(
  selected: ElementRef | null,
  category: ElementRef["category"],
  name: string
): boolean {
  return selected?.category === category && selected.name === name;
}

/** Shift-click multi-selection membership — a separate, dashed highlight from isSelected's solid "primary" ring (both can apply at once). */
export function isMultiSelected(
  multiSelected: ElementRef[] | undefined,
  category: ElementRef["category"],
  name: string
): boolean {
  return !!multiSelected?.some((r) => r.category === category && r.name === name);
}

/** Whether (category, name) is the map's single roving-tabindex target — see `useNetworkMapState`'s own `rovingRef`. */
export function isRovingTarget(
  rovingRef: ElementRef | null,
  category: ElementRef["category"],
  name: string
): boolean {
  return rovingRef?.category === category && rovingRef.name === name;
}

/** First element overall, in a fixed category order, for `rovingRef` to default to when nothing is selected yet. */
function firstElementRef(project: SwmmProject): ElementRef | null {
  if (project.subcatchments[0]) {
    return { category: "subcatchment", kind: "subcatchment", name: project.subcatchments[0].name };
  }
  if (project.links[0]) {
    return { category: "link", kind: project.links[0].kind, name: project.links[0].name };
  }
  if (project.nodes[0]) {
    return { category: "node", kind: project.nodes[0].kind, name: project.nodes[0].name };
  }
  if (project.raingages[0]) {
    return { category: "raingage", kind: "raingage", name: project.raingages[0].name };
  }
  return null;
}

export type MapDrag =
  | { kind: "node"; ref: ElementRef; point: Point }
  | { kind: "vertex"; target: VertexTarget; index: number; point: Point }
  | { kind: "insert"; target: VertexTarget; index: number; point: Point };

/**
 * Whether a vertex/midpoint handle (from `linkVertexHandles`/
 * `polygonVertexHandles`, vertexHandles.ts) is the one last moved by a
 * keyboard nudge (`App.tsx`'s own `focusedVertex`, so its handle keeps a
 * visible ring after the nudge instead of looking like nothing happened).
 * Only ever true for a "vertex" handle — a "midpoint" is a phantom insertion
 * point with no identity of its own to keep focus on. Was the same 3-line
 * expression written out identically at 4 render sites across the two map
 * components (P1 deep-review item 4) — extracted once here instead.
 */
export function isHandleFocused(
  focusedVertex: { target: VertexTarget; index: number } | null | undefined,
  handle: { kind: "vertex" | "midpoint"; target: VertexTarget; index: number }
): boolean {
  return (
    handle.kind === "vertex" &&
    focusedVertex?.target.name === handle.target.name &&
    focusedVertex.index === handle.index
  );
}

/**
 * A link's endpoint-plus-vertex point chain, or `null` if either end node's
 * position is missing (a link can't render without both — the same guard
 * `LinksLayer` in both map components, and both maps' own vertex-handle
 * rendering for the selected link, each re-implemented before this
 * extraction — P1 deep-review item 4).
 */
export function linkPathPoints(
  link: SwmmLink,
  nodePosition: ReadonlyMap<string, Point>,
  vertices: ReadonlyMap<string, Point[]>,
  effectivePoints: (target: VertexTarget, base: Point[]) => Point[]
): { from: Point; to: Point; vertices: Point[] } | null {
  const from = nodePosition.get(link.fromNode);
  const to = nodePosition.get(link.toNode);
  if (!from || !to) return null;
  const linkVertices = effectivePoints(
    { section: "VERTICES", name: link.name },
    vertices.get(link.name) ?? []
  );
  return { from, to, vertices: linkVertices };
}

/**
 * Enter/Space activates (selects) a node/raingage; arrow keys nudge it, only
 * once it's already the selection and only while geometry editing
 * (`allowDrag`) is on. SVG-only (`NetworkMap.tsx`'s `NodesLayer`/
 * `RaingagesLayer`, previously duplicated verbatim between the two) —
 * `GeoreferencedMap.tsx`'s Leaflet markers have no keyboard-nudge, only
 * Enter/Space via `selectableRef`.
 */
export function pointActivateOrNudge(
  e: React.KeyboardEvent,
  ref: ElementRef,
  point: Point,
  selected: ElementRef | null,
  allowDrag: boolean,
  markerSize: number,
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void,
  onMoveNode: (ref: ElementRef, point: Point) => void
): void {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onSelect(ref, e.shiftKey);
    return;
  }
  if (!allowDrag || !isSelected(selected, ref.category, ref.name)) return;
  const step = (e.shiftKey ? 10 : 1) * markerSize;
  if (e.key === "ArrowLeft") onMoveNode(ref, { x: point.x - step, y: point.y });
  else if (e.key === "ArrowRight") onMoveNode(ref, { x: point.x + step, y: point.y });
  else if (e.key === "ArrowUp") onMoveNode(ref, { x: point.x, y: point.y + step });
  else if (e.key === "ArrowDown") onMoveNode(ref, { x: point.x, y: point.y - step });
  else return;
  e.preventDefault();
}

/**
 * Selection/drag state shared between NetworkMap.tsx (SVG) and
 * GeoreferencedMap.tsx (Leaflet). Everything here uses the project's raw,
 * un-reprojected coordinates — each map applies its own final transform
 * (toSvg/toLL) on top of whatever this returns.
 */
export function useNetworkMapState(project: SwmmProject, selected: ElementRef | null) {
  const [drag, setDrag] = useState<MapDrag | null>(null);
  // Authoritative live value for a pointerup handler keyed on `drag !== null`
  // rather than the full `drag` value (NetworkMap.tsx's own node/vertex-drag
  // effect) — `kind`/`ref`/`target`/`index` never change once a drag starts,
  // only `.point` does (every pointermove), so a plain per-render sync
  // effect is enough; no drag-start callsite needs to write this directly.
  const dragRef = useRef<MapDrag | null>(null);
  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  // Only copy the map while a node is actively being dragged (to substitute
  // its live position) — avoids copying every entry on every render.
  const nodePosition: ReadonlyMap<string, Point> =
    drag?.kind === "node" && drag.ref.category !== "raingage"
      ? new Map(project.coordinates).set(drag.ref.name, drag.point)
      : project.coordinates;
  const symbolPosition: ReadonlyMap<string, Point> =
    drag?.kind === "node" && drag.ref.category === "raingage"
      ? new Map(project.symbols).set(drag.ref.name, drag.point)
      : project.symbols;

  // Memoized — a real network can have hundreds of polygons; only depends
  // on project.polygons, never the in-progress drag.
  const subcatchmentAnchor = useMemo(() => {
    const m = new Map<string, Point>();
    for (const [name, poly] of project.polygons) m.set(name, polygonCentroid(poly));
    return m;
  }, [project.polygons]);

  /**
   * The points a link's [VERTICES] or a subcatchment's [POLYGONS] should
   * render with right now, with the in-progress drag point substituted in
   * so the shape follows the cursor live. `useCallback`'d on [drag] alone
   * so a referentially-stable identity survives any unrelated re-render
   * (pan, lasso-select, hover) — otherwise a fresh function identity every
   * render would defeat React.memo on any child that takes it as a prop
   * (e.g. NetworkMap.tsx's SubcatchmentsLayer).
   */
  const effectivePoints = useCallback(
    (target: VertexTarget, base: Point[]): Point[] => {
      if (!drag || drag.kind === "node") return base;
      if (drag.target.section !== target.section || drag.target.name !== target.name) return base;
      const next = [...base];
      if (drag.kind === "vertex") next[drag.index] = drag.point;
      else next.splice(drag.index, 0, drag.point);
      return next;
    },
    [drag]
  );

  const selectedLink =
    selected?.category === "link" ? project.links.find((l) => l.name === selected.name) : null;
  const selectedSubcatchment =
    selected?.category === "subcatchment"
      ? project.subcatchments.find((s) => s.name === selected.name)
      : null;

  // Where each map's selection ring/pan-to-selection anchors itself — a
  // link/subcatchment use their own midpoint/centroid.
  const highlightPoint: Point | null = !selected
    ? null
    : selected.category === "node"
      ? (nodePosition.get(selected.name) ?? null)
      : selected.category === "raingage"
        ? (symbolPosition.get(selected.name) ?? null)
        : selected.category === "link" && selectedLink
          ? (() => {
              const from = nodePosition.get(selectedLink.fromNode);
              const to = nodePosition.get(selectedLink.toNode);
              return from && to ? { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 } : null;
            })()
          : selected.category === "subcatchment" && selectedSubcatchment
            ? (subcatchmentAnchor.get(selectedSubcatchment.name) ?? null)
            : null;

  // Every map element used to get its own `tabIndex={0}` — on a real
  // (thousands-of-element) network, Tab from the toolbar into the map had to
  // cycle through every visible shape before reaching anything else (P1
  // deep-review, accessibility #2). Only `rovingRef` is a real Tab stop now
  // (`isRovingTarget` above); everything else gets tabIndex=-1. It follows
  // the current selection so Tab always lands back on the last-selected
  // element, defaulting to the first element overall when nothing is
  // selected yet. Arrow keys aren't repurposed to cycle between elements
  // here — NodesLayer/RaingagesLayer's own `handlePointKeyDown` already uses
  // them to nudge the *selected* node/raingage; ElementBrowser.tsx/
  // ElementSearch.tsx (both already keyboard-reachable) are the intended way
  // to move the roving target without a mouse.
  const rovingRef = useMemo(() => selected ?? firstElementRef(project), [selected, project]);

  return {
    drag,
    setDrag,
    dragRef,
    nodePosition,
    symbolPosition,
    subcatchmentAnchor,
    effectivePoints,
    selectedLink,
    selectedSubcatchment,
    highlightPoint,
    rovingRef,
  };
}
